"use client";
import { Button } from "@cmp-src/primitives/button";
import type { ScheduleRequestUpdate } from "@gettactic/api";
import {
  Modal,
  ModalFooter,
  type ModalProps
} from "@gettactic/components/src/overlay/modals/Modal/Modal";
import { getApiErrorMessage } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addMinutes, format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { formatExtendedEndTime } from "@/features/reservation/components/ReservationCard/extendedEndTime";
import { api } from "@/lib/api/api";

const EXTEND_OPTIONS = [15, 30, 45, 60, 120, 180] as const;

export type ExtendableSchedule = {
  id: string;
  start: string;
  /** Null for a walk-up check-in — it has no end until checkout. */
  end: string | null;
  time_zone: string;
  office?: { time_zone: string } | null;
  slots: { id: string; resource: { id: string }; user: { id: string } }[];
};

type Props = Pick<ModalProps, "isOpen" | "onOpenChange" | "onClose"> & {
  schedule: ExtendableSchedule;
};

export const ExtendReservationDialog: React.FC<Props> = ({
  isOpen,
  onOpenChange,
  onClose,
  schedule
}) => {
  const [selectedMinutes, setSelectedMinutes] = useState<number>(15);

  useEffect(() => {
    if (isOpen) {
      setSelectedMinutes(15);
    }
  }, [isOpen]);

  const queryClient = useQueryClient();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  // A walk-up check-in has no end until checkout — there's nothing to
  // extend from, so the trigger that opens this dialog is already hidden
  // for that case (see SchedulesGroups/ReservationCardItem). This dialog
  // stays mounted regardless of `isOpen` though, so these must not crash
  // just from rendering with such a schedule.
  const currentEnd = schedule.end
    ? parseISO(schedule.end.replace("Z", ""))
    : null;
  const newEndFormatted = schedule.end
    ? formatExtendedEndTime({
        end: schedule.end,
        timeZone: schedule.time_zone,
        displayTimeZone: schedule.office?.time_zone ?? schedule.time_zone,
        minutes: selectedMinutes
      })
    : null;

  const extendMutation = useMutation({
    mutationFn: async (minutes: number) => {
      if (!currentEnd) {
        throw new Error("Cannot extend a reservation with no end time");
      }
      const endDate = addMinutes(currentEnd, minutes);
      const endStr = format(endDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");

      const updatePayload: ScheduleRequestUpdate = {
        schedule_slots: schedule.slots.map((slot) => ({
          id: slot.id,
          resource_id: slot.resource.id,
          start: schedule.start,
          end: endStr,
          time_zone: schedule.time_zone,
          user_id: slot.user.id
        }))
      };

      const result = await api.client.schedules.patch(
        schedule.id,
        updatePayload,
        false,
        authenticatedUser.slug ?? ""
      );

      if (!result.ok) {
        throw result;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["myAgenda"] });
      toast.success(`Reservation extended by ${selectedMinutes} minutes`);
      onClose?.();
    },
    onError: (error) => {
      toast.error(
        getApiErrorMessage(
          error,
          "Could not extend the reservation. Please try again."
        )
      );
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClose={onClose ?? (() => onOpenChange?.(false))}
      title="Extend Reservation"
      description="Select how long you would like to extend this reservation."
      size="xs"
    >
      <div className="grid grid-cols-3 gap-2">
        {EXTEND_OPTIONS.map((minutes) => (
          <Button
            key={minutes}
            type="button"
            variant={selectedMinutes === minutes ? "default" : "outline"}
            onClick={() => setSelectedMinutes(minutes)}
          >
            {minutes >= 60 ? `${minutes / 60}h` : `${minutes} min`}
          </Button>
        ))}
      </div>
      <p className="text-center text-sm text-muted-foreground">
        New end time:{" "}
        <span className="font-medium">{newEndFormatted ?? "—"}</span>
      </p>
      <ModalFooter>
        <Button
          variant="outline"
          onClick={onClose ?? (() => onOpenChange?.(false))}
        >
          Cancel
        </Button>
        <Button
          onClick={() => extendMutation.mutate(selectedMinutes)}
          disabled={extendMutation.isPending || !currentEnd}
          isLoading={extendMutation.isPending}
        >
          Extend
        </Button>
      </ModalFooter>
    </Modal>
  );
};
